import { AlertTriangle, Moon, TrendingUp, Package, Users, type LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useAiInsights } from '@/hooks/queries/useAi';
import { useMarkInsightRead } from '@/hooks/mutations/useAiMutations';
import type { AiInsight } from '@/types/ai';

interface ChatInsightAlertsProps {
  onSelectPrompt: (prompt: string) => void;
  className?: string;
}

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  stock: Package,
  sales: TrendingUp,
  customers: Users,
  digest: Moon,
};

const PRIORITY_STYLES: Record<string, string> = {
  critical: 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30',
  high: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30',
  medium: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20',
  low: 'border-charcoal-200 dark:border-charcoal-700 bg-muted/50',
};

function buildPromptFromInsight(insight: AiInsight): string {
  switch (insight.type) {
    case 'stock_alert':
      return 'Quels produits sont en rupture de stock et que dois-je commander en priorité ?';
    case 'sales_anomaly':
      return 'Il y a une anomalie dans mes ventes. Explique-moi ce qui se passe et que faire.';
    case 'revenue_trend':
      return 'Analyse la tendance de mon chiffre d\'affaires et donne-moi des recommandations.';
    case 'customer_churn':
      return 'Quels clients sont inactifs et comment les relancer ?';
    default:
      return `Détaille cette alerte : ${insight.title}`;
  }
}

export function ChatInsightAlerts({ onSelectPrompt, className }: ChatInsightAlertsProps) {
  const { data: insights } = useAiInsights({ is_read: false });
  const markRead = useMarkInsightRead();

  const unread = insights?.filter((i) => !i.is_dismissed).slice(0, 3) ?? [];

  if (unread.length === 0) return null;

  return (
    <div className={cn('w-full max-w-[320px]', className)}>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Alertes
      </p>
      <div className="flex flex-col gap-1.5">
        {unread.map((insight, i) => {
          const Icon = CATEGORY_ICONS[insight.category] ?? AlertTriangle;
          const style = PRIORITY_STYLES[insight.priority] ?? PRIORITY_STYLES.low;

          return (
            <motion.button
              key={insight.id}
              type="button"
              onClick={() => {
                markRead.mutate(insight.id);
                onSelectPrompt(buildPromptFromInsight(insight));
              }}
              className={cn(
                'flex items-start gap-2.5 rounded-lg border p-2.5 text-left active:scale-[0.98] transition-transform hover:opacity-90',
                style,
              )}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06, duration: 0.15 }}
            >
              <Icon className="mt-0.5 size-3.5 shrink-0 text-charcoal-600 dark:text-charcoal-400" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-charcoal-800 dark:text-charcoal-200 leading-tight">
                  {insight.title}
                </p>
                <p className="mt-0.5 text-[10px] text-muted-foreground line-clamp-1">
                  {insight.message}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Hook to get count of unread insights (for badge on FAB).
 */
export function useUnreadInsightsCount(): number {
  const { data: insights } = useAiInsights({ is_read: false });
  return insights?.filter((i) => !i.is_dismissed).length ?? 0;
}
